import React from "react"
import { Box, Button, List, ListItem, TextField } from "@mui/material"
import { NumericFormat } from "react-number-format"
import { PlacementEntry } from "../../../models/placement"
import { calculateMaturityFromStrength } from "../../../utilities/maturity"
import { GoalEntry } from "../../../models/goal"

interface Props {
  entry: PlacementEntry
  onGoalsChange: (goals: GoalEntry[]) => void
  disabled?: boolean
  highlightRequiredFields?: boolean
}

const GoalView = (props: Props) => {

  if (props.entry.maturityMethod.type === "disabled") {
    return null
  }

  if (!props.entry.maturityMethod.temperatureUnit) {
    return null
  }

  const goals = props.entry.goals
  const maturityUnit = props.entry.maturityMethod.type === "arrhenius" ? "hr" : `º${props.entry.maturityMethod.temperatureUnit.toUpperCase()}-hr`


  const handleGoalRemove = (index: number) => {
    goals.splice(index, 1)
    props.onGoalsChange(goals)
  }

  return (
    <>
      <List sx={{
        padding: "0px 0px",
        marginTop: "24px",
        width: "100%"
      }}>
        {goals.map((goal, i) => {
          return (
            <ListItem sx={{
              paddingLeft: "0px",
              paddingRight: "0px",
              paddingBottom: "24px"
            }} key={goal.id}>
              <Box display="flex" flexDirection="column" sx={{
                width: "100%"
              }}>
                <List sx={{
                  padding: "0px",
                  width: "100%"
                }}>
                  <ListItem sx={{
                    paddingLeft: "0px",
                    paddingRight: "0px"
                  }}>
                    <TextField
                      label="Goal Name"
                      value={goal.name}
                      onChange={(e) => {
                        goals[i].name = e.target.value
                        props.onGoalsChange(goals)
                      }}
                      fullWidth
                      disabled={props.disabled}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{
                    paddingLeft: "0px",
                    paddingRight: "0px"
                  }}>
                    <NumericFormat
                      key={`goal-${i}`}
                      customInput={TextField}
                      placeholder="Required"
                      label={props.entry.maturityMethod.curveFit.type === "disabled" ? `Maturity (${maturityUnit})` : `Strength (${props.entry.maturityMethod.curveFit.parameters.strengthUnit})`}
                      value={goal.value ?? ""}
                      thousandSeparator=","
                      decimalScale={
                        props.entry.maturityMethod.curveFit.type === "disabled"
                          ? 0
                          : props.entry.maturityMethod.curveFit.parameters.strengthUnit === "psi"
                            ? 0
                            : 2
                      }
                      fixedDecimalScale={
                        props.entry.maturityMethod.curveFit.type !== "disabled" &&
                        props.entry.maturityMethod.curveFit.parameters.strengthUnit === "mpa"
                      }
                      allowNegative={false}
                      onValueChange={(values) => {
                        goals[i].value = parseFloat(values.value) || null
                        props.onGoalsChange(goals)
                      }}
                      fullWidth
                      error={props.highlightRequiredFields && !goal.value}
                      disabled={props.disabled}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </ListItem>
                  {props.entry.maturityMethod.curveFit.type !== "disabled" &&
                    <ListItem
                      sx={{
                        paddingLeft: "0px",
                        paddingRight: "0px"
                      }}>
                      <NumericFormat
                        key={`maturity-${i}`}
                        customInput={TextField}
                        size="small"
                        label={`Relative Maturity (${maturityUnit})`}
                        value={goal.value ? calculateMaturityFromStrength(props.entry.maturityMethod, goal.value) ?? "" : ""}
                        thousandSeparator=","
                        decimalScale={0}
                        fixedDecimalScale={false}
                        allowNegative={false}
                        disabled
                        fullWidth
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </ListItem>
                  }
                </List>
                <Button
                  variant="text"
                  color="primary"
                  sx={{
                    textTransform: "none",
                    fontWeight: "normal",
                    background: "none",
                    padding: "0px",
                    whiteSpace: "nowrap",
                    minWidth: "auto",
                    alignSelf: "flex-end",
                    ":hover": { background: "none" }
                  }}
                  onClick={() => handleGoalRemove(i)}
                >
                  Remove Goal
                </Button>
              </Box>
            </ListItem>
          )
        })}
      </List>
    </>
  )
}

export default GoalView
